import {
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Tabs,
  Title,
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRecalcPlan, usePlan } from '@/api/usePlaene';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';
import { useCanWrite } from '@/auth/useRequireRole';
import { BelegungTab } from './editor/BelegungTab';
import {
  EinnahmenKommuneTab,
  EinnahmenLandTab,
  EinnahmenSonstigeTab,
} from './editor/FinanzierungTab';
import { PersonalbedarfTab } from './editor/PersonalbedarfTab';
import { PersonalkostenTab } from './editor/PersonalkostenTab';
import { SachkostenTab } from './editor/SachkostenTab';

const RECALC_DEBOUNCE_MS = 500;

export function PlanEditorPage() {
  const { t } = useTranslation(['plaene', 'common']);
  const { planId } = useParams<{ planId: string }>();
  const canWrite = useCanWrite();

  const planQuery = usePlan(planId ?? null);
  const recalc = useRecalcPlan();

  const debounceRef = useRef<number | null>(null);
  const [dirty, setDirty] = useState(false);

  const triggerDebouncedRecalc = () => {
    if (!planId) return;
    setDirty(true);
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      recalc.mutate(planId, { onSettled: () => setDirty(false) });
    }, RECALC_DEBOUNCE_MS);
  };

  useEffect(
    () => () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    },
    [],
  );

  if (!planId) return <EmptyState />;
  if (planQuery.isLoading) return <LoadingState />;
  if (planQuery.isError) return <ErrorState error={planQuery.error} />;
  const plan = planQuery.data;
  if (!plan) return <EmptyState />;

  const editorDisabled = !canWrite || plan.status === 'final';

  return (
    <Stack>
      <Group justify="space-between">
        <Stack gap={2}>
          <Title order={2}>{plan.name}</Title>
          <Group gap="xs">
            <Badge color="gray" variant="light">
              {plan.foerderjahr}
            </Badge>
            <Badge color="blue" variant="light">
              {t(`common:status.${plan.status}`)}
            </Badge>
            {dirty ? (
              <Badge color="yellow" variant="light">
                {t('plaene:editor.dirty.message')}
              </Badge>
            ) : null}
          </Group>
        </Stack>
        <Button
          leftSection={<IconRefresh size={16} />}
          variant="default"
          onClick={() => recalc.mutate(planId)}
          loading={recalc.isPending}
        >
          {t('plaene:editor.sidebar.refresh')}
        </Button>
      </Group>

      <Paper withBorder p="md">
        <Tabs defaultValue="belegung">
          <Tabs.List>
            <Tabs.Tab value="belegung">{t('plaene:editor.tab.belegung')}</Tabs.Tab>
            <Tabs.Tab value="personalkosten">{t('plaene:editor.tab.personalkosten')}</Tabs.Tab>
            <Tabs.Tab value="personalbedarf">{t('plaene:editor.tab.personalbedarf')}</Tabs.Tab>
            <Tabs.Tab value="sachkosten">{t('plaene:editor.tab.sachkosten')}</Tabs.Tab>
            <Tabs.Tab value="einnahmen-land">{t('plaene:editor.tab.einnahmenLand')}</Tabs.Tab>
            <Tabs.Tab value="einnahmen-kommune">
              {t('plaene:editor.tab.einnahmenKommune')}
            </Tabs.Tab>
            <Tabs.Tab value="einnahmen-sonstige">
              {t('plaene:editor.tab.einnahmenSonstige')}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="belegung" pt="md">
            <BelegungTab
              planId={planId}
              disabled={editorDisabled}
              onMutated={triggerDebouncedRecalc}
            />
          </Tabs.Panel>

          <Tabs.Panel value="personalkosten" pt="md">
            <PersonalkostenTab
              planId={planId}
              disabled={editorDisabled}
              onMutated={triggerDebouncedRecalc}
            />
          </Tabs.Panel>

          <Tabs.Panel value="personalbedarf" pt="md">
            <PersonalbedarfTab
              planId={planId}
              disabled={editorDisabled}
              onMutated={triggerDebouncedRecalc}
            />
          </Tabs.Panel>

          <Tabs.Panel value="sachkosten" pt="md">
            <SachkostenTab
              planId={planId}
              disabled={editorDisabled}
              onMutated={triggerDebouncedRecalc}
            />
          </Tabs.Panel>

          <Tabs.Panel value="einnahmen-land" pt="md">
            <EinnahmenLandTab
              planId={planId}
              disabled={editorDisabled}
              onMutated={triggerDebouncedRecalc}
            />
          </Tabs.Panel>

          <Tabs.Panel value="einnahmen-kommune" pt="md">
            <EinnahmenKommuneTab
              planId={planId}
              disabled={editorDisabled}
              onMutated={triggerDebouncedRecalc}
            />
          </Tabs.Panel>

          <Tabs.Panel value="einnahmen-sonstige" pt="md">
            <EinnahmenSonstigeTab
              planId={planId}
              disabled={editorDisabled}
              onMutated={triggerDebouncedRecalc}
            />
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </Stack>
  );
}
